import React from 'react'
import styled from 'styled-components'
import { useTable, usePagination } from 'react-table'




const Styles = styled.div`
  padding: 1rem;

  table {
    border-spacing: 0;
    border: 1px solid black;
    thead tr {
      display: contents;
    }
    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid black;
      border-right: 1px solid black;

      :last-child {
        border-right: 0;
      }

      input {
        font-size: 1rem;
        padding: 0;
        margin: 0;
        border: 0;
      }
    }
  }

  .pagination {
    padding: 0.5rem;
  }
`

// Create an editable cell renderer
const CheckBoxCell = ({
  value,
  row: { index },
  row,
  state: {pageIndex},
  column: { id },
  selectRow, // This is a custom function that we supplied to our table instance
}) => {
  const onChange = e => {
    selectRow(row, index, id, !value, pageIndex)
  }

  return <input type='checkbox' checked={value} onChange={onChange} />
}

// Be sure to pass our selectRow and the skipPageReset option
function Table({ columns, data, selectRow, skipPageReset }) {
  // For this example, we're using pagination to illustrate how to stop
  // the current page from resetting when our data changes
  // Otherwise, nothing is different here.
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
  } = useTable(
    {
      columns,
      data,
      // use the skipPageReset option to disable page resetting temporarily
      autoResetPage: !skipPageReset,
      // selectRow isn't part of the API, but
      // anything we put into these options will
      // automatically be available on the instance.
      // That way we can call this function from our
      // cell renderer!
      selectRow,
    },
    usePagination
  )

  // Render the UI for your table
  return (
    <>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

function App() {
  // Give state to all components of the dataMap(explained later) you want to render
  const [data, setData] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState([]);

  const [maxPageIndex, setMaxPageIndex] = React.useState(1);
  const [pageIndex, setPageIndex] = React.useState(0);

  /**
   * Slices a section of map based on pageIndex and pageSize
   * @param {Object} map 
   * @param {Number} pageIndex 
   * @param {Number} pageSize 
   * @returns {Array} data slice
   */
  function generateDataFromMap(map, pageIndex=0, pageSize=10){
    if(!map){
      return [];
    }
    const viewableIds = map.ids.slice(pageIndex*pageSize, pageIndex*pageSize+pageSize);
    return viewableIds.map((key)=>{
      return {
        id: key,
        name: map.data[key].name,
        checked: map.data[key].checked,
      }
    })
  }
  
  // You must 'useMemo' even though we are not rendering dataMap directly
  // to ensure that dataMap lives for the entirety of the component lifecycle
  const dataMap = React.useMemo(
    () => {
      // Initialize fields of dataMap
      // NOTE: it is possible to obtain 'ids' and selectedIds' from data
      // however that requires iteration which in a large array can mean long delays
      const map = {data: {}, ids: [], selectedIds: {}};

      // Generate fake data: replace with an API call outside of sandbox
      for(var i=0; i<1000000; i++){
          map.data[i] = {
              name: 'Name'+i,
              checked: false,
          }
          map.ids.push(i);
      }

      // Call React setState to render a section of our data
      setData(generateDataFromMap(map));

      // Determine the max page index based on the size of our data
      setMaxPageIndex(Math.ceil(Object.keys(map.data).length/10)-1);
      return map;
    }
  , [])

  const columns = React.useMemo(
    () => [
        {
          Header: '',
          accessor: 'checked',
          Cell: CheckBoxCell,
        },
        {
          Header: 'Id',
          accessor: 'id',
        },
        {
          Header: 'Name',
          accessor: 'name',
        },
    ],
    []
  )

  /**
   * Selects All items
   */
  const selectAll = function(){
    let keys = Object.keys(dataMap.data);

    // Iterate over all items and set their 'checked' state to true
    keys.forEach((key)=>{
      dataMap.data[key].checked = true;
      dataMap.selectedIds[key] = 1; // Setting to 1 for no reason other than to store values in map
    });


    // Copy data from our dataMap into React state
    setData(generateDataFromMap(dataMap, pageIndex));
    setSelectedIds(Object.keys(dataMap.selectedIds));
  };
  const deselectAll = function() {
    let keys = Object.keys(dataMap.data);

    // Iterate over all items and set their 'checked' state to false
    keys.forEach((key)=>{
      dataMap.data[key].checked = false;
    })

    // Set selectedIds as empty
    dataMap.selectedIds = {};

    // Copy data from our dataMap into React state
    setData(generateDataFromMap(dataMap, pageIndex));
    setSelectedIds(Object.keys(dataMap.selectedIds));
  }

/**
 * Selects the row
 * @param {*} row 
 * @param {*} rowIndex 
 * @param {*} columnId 
 * @param {*} value 
 */
  const selectRow = (row, rowIndex, columnId, value) => {
    // NOTE: it is possible to obtain selectedIds by 
    // iterating over dataMap.data however to avoid iteration we manually
    // change it as we know what its end value should be
    if(value === true) {
      dataMap.data[row.original.id].checked = true;
      dataMap.selectedIds[row.original.id] = 1;
    }
    if(value === false){
      dataMap.data[row.original.id].checked = false;
      delete dataMap.selectedIds[row.original.id];
    }

    // Copy data from our dataMap into React state
    setSelectedIds(Object.keys(dataMap.selectedIds));
    setData(generateDataFromMap(dataMap, pageIndex));
  }

  const incrementPage = () => {
    if(maxPageIndex<=pageIndex){
      return;
    }
    setPageIndex(pageIndex+1);

    // Generate slice of data to render
    setData(generateDataFromMap(dataMap, pageIndex+1))
  }

  const decrementPage = () => {
    if(pageIndex<=0){
      return;
    }
    setPageIndex(pageIndex-1);

    // Generate slice of data to render
    setData(generateDataFromMap(dataMap, pageIndex-1))
  }

  return (
    <Styles>
      <button onClick={selectAll}>Select All</button>
      <button onClick={deselectAll}>Deselect All</button>
      <Table
        columns={columns}
        data={data}
        selectRow={selectRow}
      />
      <div className="pagination">
        <button onClick={() => decrementPage()}>
          {'<'}
        </button>{' '}
        <button onClick={() => incrementPage()}>
          {'>'}
        </button>{' '}
        <span>
          Page{' '}
          <strong>
            {pageIndex + 1} of {maxPageIndex+1}
          </strong>{' '}
        </span>
    </div>
    <div>Selected: {selectedIds.length > 100 ? selectedIds.slice(0,100).join(', ') + ' ...' : selectedIds.join(', ')}</div>
    <div>Selected Count: {selectedIds.length}</div>
    </Styles>
  )
}

export default App
